import type { KeyboardEvent } from 'react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import type { SupportedLangCode } from '@/application/i18n/language'
import { useDebouncedValue } from '@/application/hooks/useDebouncedValue'
import {
  useCreateTaxonomyTerm,
  useGetTaxonomyTerms,
  type TaxonomyVocabulary,
} from '@/application/services/taxonomy.service'
import { handleApiError } from '@/infrastructure/api/client'
import { normalizeTermKey } from '@/domains/taxonomy/normalize-term'
import type { TaxonomyTerm, TaxonomyTermOption } from '@/domains/types'
import { Badge } from '@/ui/components/common/Badge'
import { classNames } from '@/ui/utils/main'

/** How many of the most used terms a focused, empty field proposes. */
const POPULAR_COUNT = 5
/** How many terms a typed query proposes. */
const SEARCH_COUNT = 10
/** The popular list is fetched wider so selected terms can be dropped from it. */
const POPULAR_FETCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 200
const MAX_TERM_LENGTH = 100

interface TaxonomyTermPickerProps {
  vocabulary?: TaxonomyVocabulary
  /** The investigation's source locale: suggestions and chips are labelled in it. */
  locale: SupportedLangCode
  value: TaxonomyTerm[]
  onChange: (terms: TaxonomyTerm[]) => void
  placeholder?: string
  disabled?: boolean
}

type PickerItem =
  | { kind: 'option'; key: string; option: TaxonomyTermOption }
  | { kind: 'create'; key: string; name: string }

function toTerm(option: TaxonomyTermOption): TaxonomyTerm {
  return {
    id: option.id,
    vocabulary: option.vocabulary,
    slug: option.slug,
    name: option.name,
    sourceLocale: option.sourceLocale,
    labels: option.labels,
  }
}

function labelIn(term: TaxonomyTerm, locale: SupportedLangCode): string {
  return term.labels[locale] ?? term.name
}

/** Every text a term is known by: its name and all its labels. */
function searchableTexts(term: TaxonomyTerm): string[] {
  return [term.name, ...Object.values(term.labels)]
}

/**
 * Combobox that links taxonomy terms to an investigation: it shows the chosen
 * terms as chips, proposes the 5 most used ones when focused with an empty
 * field, filters as the user types (instantly on the previous results, then
 * with the server search once typing pauses) and offers a "Create «x»" row when
 * nothing matches the typed name exactly. Follows the WAI-ARIA combobox
 * pattern (listbox popup, `aria-activedescendant`, polite live region).
 */
export function TaxonomyTermPicker({
  vocabulary = 'category',
  locale,
  value,
  onChange,
  placeholder,
  disabled,
}: TaxonomyTermPickerProps) {
  const { t } = useTranslation()
  const listId = useId()
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const typed = text.trim()
  const typedKey = normalizeTermKey(typed)
  const debouncedTyped = useDebouncedValue(typed, SEARCH_DEBOUNCE_MS)
  const { data: fetched } = useGetTaxonomyTerms({
    vocabulary,
    locale,
    query: debouncedTyped,
    limit: debouncedTyped ? SEARCH_COUNT : POPULAR_FETCH_LIMIT,
    enabled: open && !disabled,
  })
  const createTerm = useCreateTaxonomyTerm(vocabulary)

  const selectedIds = useMemo(() => new Set(value.map((term) => term.id)), [value])

  const items = useMemo<PickerItem[]>(() => {
    // The previous results stay until the new ones arrive: narrow them
    // locally so the list reacts to every keystroke.
    const options = (fetched ?? []).filter(
      (option) =>
        !selectedIds.has(option.id) &&
        (typedKey === '' ||
          searchableTexts(option).some((candidate) =>
            normalizeTermKey(candidate).includes(typedKey),
          )),
    )
    const visible = options.slice(0, typedKey ? SEARCH_COUNT : POPULAR_COUNT)
    const rows: PickerItem[] = visible.map((option) => ({
      kind: 'option',
      key: option.id,
      option,
    }))

    const alreadyKnown = [...(fetched ?? []), ...value].some(
      (term) =>
        normalizeTermKey(labelIn(term, locale)) === typedKey ||
        normalizeTermKey(term.name) === typedKey,
    )
    if (typedKey !== '' && !alreadyKnown) {
      rows.push({ kind: 'create', key: 'create', name: typed })
    }
    return rows
  }, [fetched, selectedIds, typedKey, typed, value, locale])

  // Close the list when clicking outside of the field (same pattern as AuthorSelector).
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const activeItemIndex = items.length === 0 ? -1 : Math.min(activeIndex, items.length - 1)
  const optionId = (index: number) => `${listId}-option-${index}`

  const addTerm = (term: TaxonomyTerm) => {
    if (!selectedIds.has(term.id)) onChange([...value, term])
    setText('')
    setActiveIndex(0)
  }

  const removeTerm = (termId: string) => onChange(value.filter((term) => term.id !== termId))

  const createAndAdd = async (name: string) => {
    try {
      const { term } = await createTerm.mutateAsync({ name, locale })
      addTerm(term)
    } catch (error: unknown) {
      toast.error(
        t('taxonomy.create_error', {
          name,
          message: handleApiError(error),
        }),
      )
    }
  }

  const pick = (item: PickerItem) => {
    if (item.kind === 'option') addTerm(toTerm(item.option))
    else void createAndAdd(item.name)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setOpen(true)
        if (items.length > 0) setActiveIndex((activeItemIndex + 1) % items.length)
        break
      case 'ArrowUp':
        event.preventDefault()
        setOpen(true)
        if (items.length > 0) setActiveIndex((activeItemIndex - 1 + items.length) % items.length)
        break
      case 'Enter': {
        // Never submit the surrounding form from the field.
        event.preventDefault()
        const item = open ? items[activeItemIndex] : undefined
        if (item) pick(item)
        break
      }
      case 'Escape':
        setOpen(false)
        break
      case 'Tab':
        setOpen(false)
        break
      case 'Backspace':
        if (text === '' && value.length > 0) removeTerm(value[value.length - 1].id)
        break
    }
  }

  const showList = open && !disabled

  return (
    <div ref={containerRef} className="relative">
      <div
        className={classNames(
          'flex flex-wrap gap-1.5 min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((term) => {
          const label = labelIn(term, locale)
          const untranslated = !term.labels[locale] && term.sourceLocale !== locale
          return (
            <Badge key={term.id} variant="secondary" className="flex items-center gap-1 pr-1">
              {label}
              {untranslated && (
                <span
                  className="rounded bg-muted px-1 text-[10px] uppercase text-muted-foreground"
                  title={t('taxonomy.untranslated_badge_title', {
                    locale: term.sourceLocale.toUpperCase(),
                  })}
                >
                  {term.sourceLocale}
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  aria-label={t('taxonomy.remove_term', { label })}
                  onClick={(event) => {
                    event.stopPropagation()
                    removeTerm(term.id)
                  }}
                  className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          )
        })}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label={t('taxonomy.search_label')}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            showList && activeItemIndex >= 0 ? optionId(activeItemIndex) : undefined
          }
          value={text}
          maxLength={MAX_TERM_LENGTH}
          onChange={(event) => {
            setText(event.target.value)
            setActiveIndex(0)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : undefined}
          className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {showList ? t('taxonomy.suggestions_count', { count: items.length }) : ''}
      </div>

      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('taxonomy.suggestions_label')}
          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md max-h-64 overflow-y-auto"
        >
          {typed === '' && items.length > 0 && (
            <li
              role="presentation"
              className="px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              {t('taxonomy.suggestions_popular')}
            </li>
          )}
          {items.length === 0 && (
            <li role="presentation" className="px-3 py-2 text-sm text-muted-foreground">
              {t('taxonomy.no_results')}
            </li>
          )}
          {items.map((item, index) => (
            <li
              key={item.key}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeItemIndex}
              onMouseDown={(event) => {
                // Keep the focus in the input: a blur would close the list before the click lands.
                event.preventDefault()
                pick(item)
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={classNames(
                'flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer',
                index === activeItemIndex && 'bg-accent text-accent-foreground',
              )}
            >
              {item.kind === 'option' ? (
                <>
                  <span lang={locale}>{item.option.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {t('taxonomy.usage_count', { count: item.option.usageCount })}
                  </span>
                </>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  {createTerm.isPending
                    ? t('taxonomy.creating')
                    : t('taxonomy.create_term', { name: item.name })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
